/**
 * Default-visible layer id lists, shared by the server and the browser. Must stay import-free:
 * siblings in this directory reach `@civilgrid/db`, which cannot be bundled in the client
 * (`packages/client` imports this module directly, never through `./index`).
 * `importFree.test.ts` enforces it.
 */

/**
 * Every leaf under the Digitized Layers section, in catalog order. Some carry
 * `config.organizationIds` and reach only their own org; org scoping, not this list, decides that.
 */
export const DIGITIZED_LAYER_IDS: readonly string[] = [
  'digitizedEasements',
  'digitizedElectricLines',
  'digitizedElectricStructures',
  'digitizedElectricVaults',
  'digitizedFuelLines',
  'digitizedFuelPoints',
  'digitizedGasLines',
  'digitizedGasPoints',
  'digitizedOtherLines',
  'digitizedOtherPoints',
  'digitizedRailroadLines',
  'digitizedRailroadPoints',
  'digitizedRecycledWaterLines',
  'digitizedRecycledWaterPoints',
  'digitizedSewerLines',
  'digitizedSewerPoints',
  'digitizedStormLines',
  'digitizedStormPoints',
  'digitizedTelcoLines',
  'digitizedTelcoPoints',
  'digitizedTrafficSignalLines',
  'digitizedTrafficSignals',
  'digitizedWaterLines',
  'digitizedWaterPoints',
];

/**
 * App-wide default-visible layer ids, all `tile_url` leaves. Server side: the CivilGrid fallback
 * seed set (`buildSeedLayerConfig`) when the org has no usable defaults of its own. Client side:
 * the Library's "Reset Layers" target, the admin org-default membership, and the `LayerStore`
 * constructor's visibility default. Shared here so the two sides can't drift.
 */
export const DEFAULT_VISIBLE_LAYERS: readonly string[] = [
  'electricDistributionLines',
  'electricTransmissionLines',
  'gasDistribution',
  'fossilFuelPipelines',
  'sanitaryPipes',
  'waterMain',
  'waterService',
  'recycledWaterPipes',
  'stormPipes',
  'fiberRoutes',
  // Site Data overlays — on by default at seed time; the Library hides the group.
  'fieldNotes',
  'potholes',
  // Every digitized id individually, not one anchor id: an anchor would pull the rest of the
  // bucket in present-but-unchecked, and digitized uploads must draw on a first map view.
  ...DIGITIZED_LAYER_IDS,
];
